import { getTermsValue, resetTerms, setTerms, TJsonQuery } from '../../../../JsonQuery';
import { LEGACY_TO_UI_BUILDING_TYPE } from '../../../../JsonQuery/utils/mapBuildingType';
import { TTagsManager } from '../../types/tag';
import { createTagValue } from '../../utils/createTagValue';
import { EBuildingType } from '../../../../JsonQuery/types/buildingType';
import { EBuildingTypeLegacy } from '../../../../api-models/common/json_query';

const ID = 'buildingType' as const;
const FIELDS = ['building_type__building', 'building_type2'] as const;

const UI_LABELS: Record<EBuildingType, string> = {
  [EBuildingType.BusinessCenter]: 'Бизнес-центр',
  [EBuildingType.House]: 'Особняк',
  [EBuildingType.AdministrativeBuilding]: 'Административное здание',
  [EBuildingType.MultifunctionalComplex]: 'Многофункциональный комплекс',
  [EBuildingType.Warehouse]: 'Склад/логистика',
  [EBuildingType.FAO]: 'Помещение свободного назначения',
  [EBuildingType.Other]: 'Прочее',
  [EBuildingType.ShoppingCenter]: 'Площадь в торговом центре',
  [EBuildingType.ResidentialBuilding]: 'Жилое здание',
  [EBuildingType.OfficeBuilding]: 'Офисное здание',
};

const UI_TO_LEGACY: Record<EBuildingType, EBuildingTypeLegacy[]> = (() => {
  const map: Partial<Record<EBuildingType, EBuildingTypeLegacy[]>> = {};
  for (const [legacyStr, ui] of Object.entries(LEGACY_TO_UI_BUILDING_TYPE)) {
    const legacy = Number(legacyStr) as EBuildingTypeLegacy;
    const list = map[ui] ?? [];
    list.push(legacy);
    map[ui] = list;
  }

  return map as Record<EBuildingType, EBuildingTypeLegacy[]>;
})();

const removeLegacyValuesFromField =
  (field: (typeof FIELDS)[number], legacyToRemove: EBuildingTypeLegacy[]) =>
  (jsonQuery: TJsonQuery): TJsonQuery => {
    const current = (getTermsValue(field)(jsonQuery) ?? []) as EBuildingTypeLegacy[];
    if (current.length === 0) {
      return jsonQuery;
    }
    const next = current.filter(v => !legacyToRemove.includes(v));
    if (next.length === current.length) {
      return jsonQuery;
    }

    return next.length > 0 ? setTerms(field)(jsonQuery)(next) : resetTerms([field])(jsonQuery);
  };

export const buildingTypeTagsManager: TTagsManager = {
  id: ID,
  extractTagsFromJsonQuery: ({ jsonQuery }) => {
    const building = (getTermsValue('building_type__building')(jsonQuery) ?? []) as EBuildingTypeLegacy[];
    const generic = (getTermsValue('building_type2')(jsonQuery) ?? []) as EBuildingTypeLegacy[];
    const merged = new Set<EBuildingType>();

    for (const value of [...building, ...generic]) {
      const ui = LEGACY_TO_UI_BUILDING_TYPE[value];
      if (ui !== undefined) {
        merged.add(ui);
      }
    }

    if (merged.size === 0) {
      return null;
    }

    return Array.from(merged).map(t => ({ label: UI_LABELS[t], value: createTagValue(ID, t.toString()) }));
  },
  modifyJsonQueryByRemovingTag: ({ jsonQuery, removingTagValue }) => {
    const removed = Number(removingTagValue) as EBuildingType;
    const legacyToRemove = UI_TO_LEGACY[removed] ?? [];

    return FIELDS.reduce((acc, field) => removeLegacyValuesFromField(field, legacyToRemove)(acc), jsonQuery);
  },
};
